import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import
{
  FiShield, FiPlus, FiEdit2, FiTrash2, FiX, FiSave,
  FiCheck, FiSearch, FiLock, FiAlertTriangle
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { userAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const SYSTEM_ROLES = [ 'Super Admin', 'Admin', 'Manager', 'Operator', 'Viewer' ];

const roleColors = {
  'Super Admin': { bg: '#fce7f3', color: '#9d174d' },
  'Admin': { bg: '#fef3c7', color: '#92400e' },
  'Manager': { bg: '#dbeafe', color: '#1e40af' },
  'Operator': { bg: '#dcfce7', color: '#166534' },
  'Viewer': { bg: '#f3e8ff', color: '#6b21a8' },
};

const defaultRoleColor = { bg: '#e0f2fe', color: '#0369a1' };

const styles = {
  container: { fontFamily: 'Inter, sans-serif' },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 28, flexWrap: 'wrap', gap: 16,
  },
  title: {
    fontFamily: 'Poppins, sans-serif', fontSize: 28, fontWeight: 800,
    color: '#064e3b', margin: 0,
  },
  subtitle: { fontSize: 14, color: '#6b7280', marginTop: 4 },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px',
    background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white',
    border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif', whiteSpace: 'nowrap',
  },
  glassCard: {
    background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(20px)',
    borderRadius: 20, border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 8px 32px rgba(6, 78, 59, 0.08)', overflow: 'hidden',
  },
  tabBar: {
    display: 'flex', gap: 0, borderBottom: '2px solid rgba(16, 185, 129, 0.15)',
    marginBottom: 24,
  },
  tab: {
    padding: '14px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    border: 'none', background: 'transparent', color: '#6b7280',
    borderBottom: '3px solid transparent', marginBottom: -2,
    fontFamily: 'Poppins, sans-serif', transition: 'all 0.2s ease',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  tabActive: {
    color: '#059669', borderBottomColor: '#059669', background: 'rgba(16, 185, 129, 0.04)',
  },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0 },
  th: {
    padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.8, color: '#047857',
    background: 'rgba(240, 253, 244, 0.8)', borderBottom: '2px solid rgba(16, 185, 129, 0.15)',
  },
  td: {
    padding: '14px 16px', fontSize: 14, color: '#1f2937',
    borderBottom: '1px solid rgba(16, 185, 129, 0.08)', verticalAlign: 'middle',
  },
  actionBtn: {
    width: 34, height: 34, borderRadius: 8, border: 'none', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s ease', marginRight: 6,
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(6, 78, 59, 0.5)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'white', borderRadius: 24, padding: '32px', width: '95%',
    maxWidth: 700, maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)', position: 'relative',
  },
  modalTitle: {
    fontFamily: 'Poppins, sans-serif', fontSize: 22, fontWeight: 700,
    color: '#064e3b', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10,
  },
  fieldLabel: {
    display: 'block', fontSize: 12, fontWeight: 600, color: '#047857',
    marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4,
  },
  fieldInput: {
    width: '100%', padding: '10px 14px', border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10, fontSize: 14, outline: 'none', transition: 'all 0.2s ease',
    background: '#f0fdf4', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box',
  },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '12px 32px',
    background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white',
    border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600,
    cursor: 'pointer', boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
  },
  cancelBtn: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px',
    background: 'transparent', color: '#6b7280', border: '2px solid #e5e7eb',
    borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
  },
  badge: {
    display: 'inline-block', padding: '4px 10px', borderRadius: 8,
    fontSize: 12, fontWeight: 600, marginRight: 6, marginBottom: 4,
  },
  searchWrapper: { position: 'relative', maxWidth: 400, marginBottom: 20 },
  searchIcon: {
    position: 'absolute', left: 14, top: '50%',
    transform: 'translateY(-50%)', color: '#059669',
  },
  searchInput: {
    width: '100%', padding: '12px 14px 12px 44px',
    border: '2px solid rgba(16, 185, 129, 0.2)', borderRadius: 12,
    fontSize: 14, outline: 'none', background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(8px)', transition: 'all 0.2s ease',
    fontFamily: 'Inter, sans-serif',
  },
  permCheckbox: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    borderRadius: 8, border: '1px solid rgba(16, 185, 129, 0.15)',
    background: '#f0fdf4', cursor: 'pointer', transition: 'all 0.2s ease',
    fontSize: 13,
  },
  permCheckboxActive: {
    borderColor: '#10b981', background: 'rgba(16, 185, 129, 0.08)',
    boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.1)',
  },
};

export default function RolePermissionManagement ()
{
  const { isSuperAdmin } = useAuth();
  const [ activeTab, setActiveTab ] = useState( 'roles' );
  const [ roles, setRoles ] = useState( [] );
  const [ permissions, setPermissions ] = useState( [] );
  const [ loading, setLoading ] = useState( true );
  const [ search, setSearch ] = useState( '' );

  // Role modal
  const [ roleModal, setRoleModal ] = useState( false );
  const [ editingRole, setEditingRole ] = useState( null );
  const [ roleForm, setRoleForm ] = useState( { role_name: '', description: '', permission_ids: [] } );
  const [ saving, setSaving ] = useState( false );

  // Permission modal
  const [ permModal, setPermModal ] = useState( false );
  const [ editingPerm, setEditingPerm ] = useState( null );
  const [ permForm, setPermForm ] = useState( { module_name: '', action: '', description: '' } );

  // Delete confirmation
  const [ deleteModal, setDeleteModal ] = useState( null );
  const [ deleteType, setDeleteType ] = useState( '' );

  const fetchRoles = useCallback( async () =>
  {
    try
    {
      const res = await userAPI.getRoles();
      setRoles( res.data?.roles || res.data || [] );
    } catch
    {
      toast.error( 'Failed to load roles' );
    }
  }, [] );

  const fetchPermissions = useCallback( async () =>
  {
    try
    {
      const res = await userAPI.getPermissions();
      setPermissions( res.data?.permissions || res.data || [] );
    } catch
    {
      toast.error( 'Failed to load permissions' );
    }
  }, [] );

  useEffect( () =>
  {
    Promise.all( [ fetchRoles(), fetchPermissions() ] ).finally( () => setLoading( false ) );
  }, [ fetchRoles, fetchPermissions ] );

  // ── Group permissions by module ──
  const groupedPermissions = permissions.reduce( ( acc, perm ) =>
  {
    const mod = perm.module_name || 'other';
    if ( !acc[ mod ] ) acc[ mod ] = [];
    acc[ mod ].push( perm );
    return acc;
  }, {} );

  // ── Filtering ──
  const filteredRoles = roles.filter( r =>
  {
    if ( !search.trim() ) return true;
    const q = search.toLowerCase();
    return ( r.role_name || '' ).toLowerCase().includes( q ) || ( r.description || '' ).toLowerCase().includes( q );
  } );

  const filteredPermissions = permissions.filter( p =>
  {
    if ( !search.trim() ) return true;
    const q = search.toLowerCase();
    return ( p.module_name || '' ).toLowerCase().includes( q ) || ( p.action || '' ).toLowerCase().includes( q ) || ( p.description || '' ).toLowerCase().includes( q );
  } );

  // ── Role handlers ──
  const openCreateRole = () =>
  {
    setEditingRole( null );
    setRoleForm( { role_name: '', description: '', permission_ids: [] } );
    setRoleModal( true );
  };

  const openEditRole = ( role ) =>
  {
    if ( role.role_name === 'Super Admin' )
    {
      toast.warning( 'Super Admin role cannot be edited' );
      return;
    }
    setEditingRole( role );
    setRoleForm( {
      role_name: role.role_name || '',
      description: role.description || '',
      permission_ids: ( role.permissions || [] ).map( p => p.id ),
    } );
    setRoleModal( true );
  };

  const handleSaveRole = async ( e ) =>
  {
    e.preventDefault();
    if ( !roleForm.role_name.trim() )
    {
      toast.error( 'Role name is required' );
      return;
    }
    setSaving( true );
    try
    {
      if ( editingRole )
      {
        await userAPI.updateRole( editingRole.id, roleForm );
        toast.success( 'Role updated successfully' );
      } else
      {
        await userAPI.createRole( roleForm );
        toast.success( 'Role created successfully' );
      }
      setRoleModal( false );
      fetchRoles();
    } catch ( err )
    {
      toast.error( err.response?.data?.error || 'Failed to save role' );
    } finally
    {
      setSaving( false );
    }
  };

  // ── Permission handlers ──
  const openCreatePerm = () =>
  {
    setEditingPerm( null );
    setPermForm( { module_name: '', action: '', description: '' } );
    setPermModal( true );
  };

  const openEditPerm = ( perm ) =>
  {
    setEditingPerm( perm );
    setPermForm( {
      module_name: perm.module_name || '',
      action: perm.action || '',
      description: perm.description || '',
    } );
    setPermModal( true );
  };

  const handleSavePerm = async ( e ) =>
  {
    e.preventDefault();
    if ( !permForm.module_name.trim() || !permForm.action.trim() )
    {
      toast.error( 'Module name and action are required' );
      return;
    }
    setSaving( true );
    try
    {
      if ( editingPerm )
      {
        await userAPI.updatePermission( editingPerm.id, permForm );
        toast.success( 'Permission updated successfully' );
      } else
      {
        await userAPI.createPermission( permForm );
        toast.success( 'Permission created successfully' );
      }
      setPermModal( false );
      fetchPermissions();
    } catch ( err )
    {
      toast.error( err.response?.data?.error || 'Failed to save permission' );
    } finally
    {
      setSaving( false );
    }
  };

  // ── Delete handlers ──
  const confirmDelete = ( item, type ) =>
  {
    setDeleteModal( item );
    setDeleteType( type );
  };

  const handleDelete = async () =>
  {
    if ( !deleteModal ) return;
    try
    {
      if ( deleteType === 'role' )
      {
        await userAPI.deleteRole( deleteModal.id );
        toast.success( 'Role deleted' );
        fetchRoles();
      } else
      {
        await userAPI.deletePermission( deleteModal.id );
        toast.success( 'Permission deleted' );
        fetchPermissions();
      }
      setDeleteModal( null );
    } catch ( err )
    {
      toast.error( err.response?.data?.error || 'Failed to delete' );
    }
  };

  // ── Toggle permission in role form ──
  const togglePermission = ( permId ) =>
  {
    setRoleForm( prev => ( {
      ...prev,
      permission_ids: prev.permission_ids.includes( permId )
        ? prev.permission_ids.filter( id => id !== permId )
        : [ ...prev.permission_ids, permId ],
    } ) );
  };

  const selectAllModulePerms = ( modulePerms ) =>
  {
    const ids = modulePerms.map( p => p.id );
    const allSelected = ids.every( id => roleForm.permission_ids.includes( id ) );
    setRoleForm( prev => ( {
      ...prev,
      permission_ids: allSelected
        ? prev.permission_ids.filter( id => !ids.includes( id ) )
        : [ ...new Set( [ ...prev.permission_ids, ...ids ] ) ],
    } ) );
  };

  if ( !isSuperAdmin )
  {
    return (
      <div style={ { ...styles.container, textAlign: 'center', padding: '80px 20px' } }>
        <FiLock size={ 48 } color="#9ca3af" style={ { marginBottom: 16 } } />
        <h2 style={ { color: '#374151', fontFamily: 'Poppins, sans-serif' } }>Access Denied</h2>
        <p style={ { color: '#6b7280' } }>Only Super Admin can manage roles and permissions.</p>
      </div>
    );
  }

  return (
    <div style={ styles.container }>
      {/* Header */ }
      <motion.div
        initial={ { opacity: 0, y: -15 } }
        animate={ { opacity: 1, y: 0 } }
        transition={ { duration: 0.4 } }
        style={ styles.header }
      >
        <div>
          <h1 style={ styles.title }>Role Management</h1>
          <p style={ styles.subtitle }>Manage roles and assign permissions</p>
        </div>
        <div style={ { display: 'flex', gap: 10 } }>
          <motion.button
            whileHover={ { scale: 1.04 } }
            whileTap={ { scale: 0.96 } }
            style={ styles.addBtn }
            onClick={ openCreateRole }
          >
            <FiPlus size={ 18 } />
            Add Role
          </motion.button>
        </div>
      </motion.div>

      {/* Search */ }
      <motion.div initial={ { opacity: 0, y: -10 } } animate={ { opacity: 1, y: 0 } } transition={ { duration: 0.3, delay: 0.1 } }>
        <div style={ styles.searchWrapper }>
          <FiSearch size={ 18 } style={ styles.searchIcon } />
          <input
            type="text"
            placeholder="Search roles..."
            value={ search }
            onChange={ ( e ) => setSearch( e.target.value ) }
            style={ styles.searchInput }
            onFocus={ ( e ) => { e.target.style.borderColor = '#10b981'; e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.12)'; } }
            onBlur={ ( e ) => { e.target.style.borderColor = 'rgba(16, 185, 129, 0.2)'; e.target.style.boxShadow = 'none'; } }
          />
        </div>
      </motion.div>

      {/* Content */ }
      <motion.div
        initial={ { opacity: 0, y: 12 } }
        animate={ { opacity: 1, y: 0 } }
        transition={ { duration: 0.35, delay: 0.2 } }
        style={ styles.glassCard }
      >
        { loading ? (
          <div style={ { textAlign: 'center', padding: 60 } }>
            <div style={ {
              width: 40, height: 40, border: '3px solid rgba(16, 185, 129, 0.2)',
              borderTopColor: '#10b981', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
            } } />
            <p style={ { color: '#6b7280', fontSize: 14 } }>Loading...</p>
          </div>
        ) : (
          /* ── Roles Tab ── */
          filteredRoles.length === 0 ? (
            <div style={ { textAlign: 'center', padding: '60px 20px', color: '#9ca3af' } }>
              <FiShield size={ 48 } style={ { marginBottom: 16, opacity: 0.4 } } />
              <p style={ { fontSize: 16, fontWeight: 600, marginBottom: 4 } }>
                { search ? 'No roles match your search' : 'No roles found' }
              </p>
            </div>
          ) : (
            <div style={ { overflowX: 'auto' } }>
              <table style={ styles.table }>
                <thead>
                  <tr>
                    <th style={ styles.th }>Role Name</th>
                    <th style={ styles.th }>Description</th>
                    <th style={ styles.th }>Permissions</th>
                    <th style={ styles.th }>Type</th>
                    <th style={ { ...styles.th, textAlign: 'center' } }>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  { filteredRoles.map( ( role, idx ) =>
                  {
                    const isSystem = SYSTEM_ROLES.includes( role.role_name );
                    const colors = roleColors[ role.role_name ] || defaultRoleColor;
                    return (
                      <motion.tr
                        key={ role.id }
                        initial={ { opacity: 0, y: 8 } }
                        animate={ { opacity: 1, y: 0 } }
                        transition={ { delay: idx * 0.03 } }
                        onMouseEnter={ e => e.currentTarget.style.background = 'rgba(240, 253, 244, 0.5)' }
                        onMouseLeave={ e => e.currentTarget.style.background = 'transparent' }
                      >
                        <td style={ styles.td }>
                          <span style={ { ...styles.badge, background: colors.bg, color: colors.color } }>
                            { role.role_name }
                          </span>
                        </td>
                        <td style={ styles.td }>{ role.description || '--' }</td>
                        <td style={ styles.td }>
                          <span style={ {
                            padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            background: 'rgba(16, 185, 129, 0.1)', color: '#059669',
                          } }>
                            { ( role.permissions || [] ).length } permissions
                          </span>
                        </td>
                        <td style={ styles.td }>
                          <span style={ {
                            padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            background: isSystem ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                            color: isSystem ? '#92400e' : '#059669',
                          } }>
                            { isSystem ? 'System' : 'Custom' }
                          </span>
                        </td>
                        <td style={ { ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' } }>
                          <button
                            title="Edit"
                            onClick={ () => openEditRole( role ) }
                            disabled={ role.role_name === 'Super Admin' }
                            style={ {
                              ...styles.actionBtn,
                              background: role.role_name === 'Super Admin' ? '#f3f4f6' : 'rgba(16, 185, 129, 0.1)',
                              color: role.role_name === 'Super Admin' ? '#9ca3af' : '#059669',
                              cursor: role.role_name === 'Super Admin' ? 'not-allowed' : 'pointer',
                            } }
                          >
                            <FiEdit2 size={ 15 } />
                          </button>
                          { !isSystem && (
                            <button
                              title="Delete"
                              onClick={ () => confirmDelete( role, 'role' ) }
                              style={ { ...styles.actionBtn, background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' } }
                              onMouseEnter={ e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)' }
                              onMouseLeave={ e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)' }
                            >
                              <FiTrash2 size={ 15 } />
                            </button>
                          ) }
                        </td>
                      </motion.tr>
                    );
                  } ) }
                </tbody>
              </table>
            </div>
          )
        ) }
      </motion.div>

      {/* ── Role Modal ── */ }
      <AnimatePresence>
        { roleModal && (
          <motion.div
            initial={ { opacity: 0 } } animate={ { opacity: 1 } } exit={ { opacity: 0 } }
            style={ styles.overlay } onClick={ () => setRoleModal( false ) }
          >
            <motion.div
              initial={ { opacity: 0, scale: 0.93, y: 20 } }
              animate={ { opacity: 1, scale: 1, y: 0 } }
              exit={ { opacity: 0, scale: 0.93, y: 20 } }
              transition={ { duration: 0.25 } }
              style={ styles.modal }
              onClick={ e => e.stopPropagation() }
            >
              <button
                onClick={ () => setRoleModal( false ) }
                style={ {
                  position: 'absolute', top: 16, right: 16,
                  background: 'rgba(239, 68, 68, 0.08)', border: 'none',
                  borderRadius: 8, width: 36, height: 36,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#dc2626',
                } }
              >
                <FiX size={ 18 } />
              </button>

              <div style={ styles.modalTitle }>
                <div style={ {
                  width: 40, height: 40, borderRadius: 12,
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                } }>
                  { editingRole ? <FiEdit2 size={ 18 } color="white" /> : <FiPlus size={ 18 } color="white" /> }
                </div>
                { editingRole ? 'Edit Role' : 'Create New Role' }
              </div>

              <form onSubmit={ handleSaveRole }>
                <div style={ { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 } }>
                  <div>
                    <label style={ styles.fieldLabel }>Role Name *</label>
                    <input
                      type="text"
                      value={ roleForm.role_name }
                      onChange={ e => setRoleForm( prev => ( { ...prev, role_name: e.target.value } ) ) }
                      style={ styles.fieldInput }
                      placeholder="e.g. Data Entry"
                      required
                    />
                  </div>
                  <div>
                    <label style={ styles.fieldLabel }>Description</label>
                    <input
                      type="text"
                      value={ roleForm.description }
                      onChange={ e => setRoleForm( prev => ( { ...prev, description: e.target.value } ) ) }
                      style={ styles.fieldInput }
                      placeholder="Role description"
                    />
                  </div>
                </div>

                {/* Permission assignment */ }
                <div>
                  <label style={ { ...styles.fieldLabel, marginBottom: 12 } }>
                    <FiShield size={ 13 } style={ { marginRight: 6, verticalAlign: 'middle' } } />
                    Assign Permissions
                  </label>

                  <div style={ {
                    maxHeight: 350, overflowY: 'auto', padding: 2,
                    border: '1px solid rgba(16, 185, 129, 0.1)', borderRadius: 12,
                  } }>
                    { Object.entries( groupedPermissions ).map( ( [ moduleName, modulePerms ] ) =>
                    {
                      const allSelected = modulePerms.every( p => roleForm.permission_ids.includes( p.id ) );
                      const someSelected = modulePerms.some( p => roleForm.permission_ids.includes( p.id ) );
                      return (
                        <div key={ moduleName } style={ { padding: '12px 16px', borderBottom: '1px solid rgba(16, 185, 129, 0.06)' } }>
                          <div
                            onClick={ () => selectAllModulePerms( modulePerms ) }
                            style={ {
                              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                              cursor: 'pointer', userSelect: 'none',
                            } }
                          >
                            <div style={ {
                              width: 20, height: 20, borderRadius: 6,
                              border: allSelected ? '2px solid #10b981' : someSelected ? '2px solid #10b981' : '2px solid #d1d5db',
                              background: allSelected ? 'linear-gradient(135deg, #059669, #10b981)' : 'white',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            } }>
                              { allSelected && <FiCheck size={ 12 } color="white" /> }
                              { !allSelected && someSelected && <div style={ { width: 8, height: 2, background: '#10b981', borderRadius: 1 } } /> }
                            </div>
                            <span style={ {
                              fontSize: 13, fontWeight: 700, color: '#047857',
                              textTransform: 'capitalize',
                            } }>
                              { moduleName }
                            </span>
                            <span style={ { fontSize: 11, color: '#9ca3af' } }>
                              ({ modulePerms.filter( p => roleForm.permission_ids.includes( p.id ) ).length }/{ modulePerms.length })
                            </span>
                          </div>
                          <div style={ { display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 30 } }>
                            { modulePerms.map( perm =>
                            {
                              const active = roleForm.permission_ids.includes( perm.id );
                              return (
                                <div
                                  key={ perm.id }
                                  onClick={ () => togglePermission( perm.id ) }
                                  style={ {
                                    ...styles.permCheckbox,
                                    ...( active ? styles.permCheckboxActive : {} ),
                                  } }
                                >
                                  <div style={ {
                                    width: 16, height: 16, borderRadius: 4,
                                    border: active ? '2px solid #10b981' : '2px solid #d1d5db',
                                    background: active ? '#10b981' : 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                  } }>
                                    { active && <FiCheck size={ 10 } color="white" /> }
                                  </div>
                                  <span style={ { textTransform: 'capitalize', color: active ? '#064e3b' : '#6b7280' } }>
                                    { perm.action }
                                  </span>
                                </div>
                              );
                            } ) }
                          </div>
                        </div>
                      );
                    } ) }
                  </div>
                </div>

                <div style={ { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 28 } }>
                  <button type="button" style={ styles.cancelBtn } onClick={ () => setRoleModal( false ) }>Cancel</button>
                  <motion.button
                    whileHover={ { scale: 1.03 } } whileTap={ { scale: 0.97 } }
                    type="submit"
                    style={ { ...styles.saveBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer' } }
                    disabled={ saving }
                  >
                    <FiSave size={ 16 } />
                    { saving ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role' }
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        ) }
      </AnimatePresence>

      {/* ── Delete Confirmation Modal ── */ }
      <AnimatePresence>
        { deleteModal && (
          <motion.div
            initial={ { opacity: 0 } } animate={ { opacity: 1 } } exit={ { opacity: 0 } }
            style={ styles.overlay } onClick={ () => setDeleteModal( null ) }
          >
            <motion.div
              initial={ { opacity: 0, scale: 0.9 } }
              animate={ { opacity: 1, scale: 1 } }
              exit={ { opacity: 0, scale: 0.9 } }
              style={ { ...styles.modal, maxWidth: 440, textAlign: 'center', padding: '40px 32px' } }
              onClick={ e => e.stopPropagation() }
            >
              <div style={ {
                width: 60, height: 60, borderRadius: 16,
                background: 'rgba(239, 68, 68, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              } }>
                <FiAlertTriangle size={ 28 } color="#dc2626" />
              </div>
              <h3 style={ {
                fontFamily: 'Poppins, sans-serif', fontSize: 20, fontWeight: 700,
                color: '#1f2937', marginBottom: 8,
              } }>
                Delete { deleteType === 'role' ? 'Role' : 'Permission' }?
              </h3>
              <p style={ { color: '#6b7280', fontSize: 14, marginBottom: 28 } }>
                Are you sure you want to delete{ ' ' }
                <strong style={ { color: '#064e3b' } }>
                  { deleteType === 'role' ? deleteModal.role_name : `${ deleteModal.module_name }:${ deleteModal.action }` }
                </strong>
                ? This action cannot be undone.
              </p>
              <div style={ { display: 'flex', gap: 12, justifyContent: 'center' } }>
                <button style={ styles.cancelBtn } onClick={ () => setDeleteModal( null ) }>Cancel</button>
                <motion.button
                  whileHover={ { scale: 1.03 } } whileTap={ { scale: 0.97 } }
                  style={ {
                    ...styles.saveBtn,
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                  } }
                  onClick={ handleDelete }
                >
                  <FiTrash2 size={ 16 } />
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        ) }
      </AnimatePresence>
    </div>
  );
}
